import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoWithText } from '../components/Logo';
import { ArrowRight, Phone, MapPin, WhatsappLogo, List, X, Check } from 'phosphor-react';
import { toast } from 'sonner';
import './LandingPage.css';

export default function ContactPage() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', school: '', message: '' });

  const handleChange = e => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recipientEmail: 'sasbaws22@gmail.com' }),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      toast.success("Message sent — we'll reply within 24 hours.");
      setFormData({ name: '', email: '', phone: '', school: '', message: '' });
    } catch {
      toast.error('Failed to send. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: 'var(--land-white)', color: 'var(--land-text)', fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* Nav */}
      <nav className={`lp-nav${scrolled ? ' scrolled' : ''}`} style={{ background: 'rgba(255,255,255,0.97)', borderBottom: '1px solid var(--land-border)' }}>
        <div className="lp-nav-inner">
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <LogoWithText size={30} />
          </button>
          <div className="lp-nav-links">
            {[['/', 'Home'], ['/about', 'About'], ['/features', 'Features'], ['/documentation', 'Docs']].map(([path, label]) => (
              <button key={path} className="lp-nav-link" onClick={() => navigate(path)}>{label}</button>
            ))}
          </div>
          <div className="lp-nav-actions">
            <button className="lp-btn-primary" onClick={() => navigate('/login')}>
              Sign In <ArrowRight size={15} weight="bold" />
            </button>
          </div>
          <button
            onClick={() => setMenuOpen(true)}
            className="lp-hamburger"
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          >
            <List size={26} color="var(--land-text)" />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="lp-mobile-menu">
          <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}><X size={20} /></button>
          {[['/', 'Home'], ['/about', 'About'], ['/features', 'Features'], ['/documentation', 'Docs']].map(([path, label]) => (
            <button key={path} className="lp-mobile-link" onClick={() => { navigate(path); setMenuOpen(false); }}>{label}</button>
          ))}
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 24, borderTop: '1px solid var(--land-border)' }}>
            <button className="lp-btn-primary-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/login'); setMenuOpen(false); }}>Sign In</button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section style={{ paddingTop: 120, paddingBottom: 64, textAlign: 'center', background: 'var(--land-off)', borderBottom: '1px solid var(--land-border)' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 24px' }}>
          <p className="lp-section-label" style={{ justifyContent: 'center', display: 'flex' }}>Contact</p>
          <h1 style={{ fontSize: 'clamp(32px,5vw,52px)', fontWeight: 800, color: 'var(--land-text)', letterSpacing: '-0.03em', fontFamily: 'Manrope, sans-serif', marginBottom: 16, lineHeight: 1.1 }}>
            We'd love to hear from you.
          </h1>
          <p style={{ fontSize: 18, color: 'var(--land-muted)', lineHeight: 1.65 }}>
            Questions about pricing, onboarding, or anything else — our team is ready to help.
          </p>
        </div>
      </section>

      {/* Contact cards + form */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 48, alignItems: 'start' }}>

          {/* Left: info */}
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--land-text)', fontFamily: 'Manrope, sans-serif', marginBottom: 32 }}>
              Get in touch
            </h2>

            {[
              { icon: Phone, title: 'Phone', value: '+233 53 448 4781', sub: 'Mon–Fri, 9AM–5PM GMT', href: 'tel:+233534484781' },
              { icon: WhatsappLogo, title: 'WhatsApp', value: '+233 53 448 4781', sub: 'Message us directly', href: 'https://wa.me/233534484781' },
              { icon: MapPin, title: 'Location', value: 'Accra, Ghana', sub: 'West Africa', href: null },
            ].map(({ icon: Icon, title, value, sub, href }) => (
              <div
                key={title}
                onClick={() => href && window.open(href, '_blank')}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 16,
                  padding: '20px 0', borderBottom: '1px solid var(--land-border)',
                  cursor: href ? 'pointer' : 'default',
                }}
              >
                <div style={{ width: 40, height: 40, background: 'var(--land-stone)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--land-forest)', flexShrink: 0 }}>
                  <Icon size={20} weight="duotone" />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--land-muted)', marginBottom: 2 }}>{title}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--land-text)' }}>{value}</div>
                  <div style={{ fontSize: 13, color: 'var(--land-muted)' }}>{sub}</div>
                </div>
              </div>
            ))}

            <div style={{ marginTop: 40, padding: 24, background: 'var(--land-stone)', borderRadius: 12, border: '1px solid var(--land-border)' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--land-text)', marginBottom: 8 }}>Quick links</div>
              {[['Documentation', '/documentation'], ['Feature Overview', '/features'], ['About Us', '/about']].map(([label, path]) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', fontSize: 14, color: 'var(--land-moss)', fontWeight: 500 }}
                >
                  <ArrowRight size={13} weight="bold" /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Right: form */}
          <div style={{ background: 'white', border: '1px solid var(--land-border)', borderRadius: 16, padding: 40 }}>
            {sent ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ width: 56, height: 56, background: 'var(--land-stone)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: 'var(--land-moss)' }}>
                  <Check size={28} weight="bold" />
                </div>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: 'var(--land-text)', fontFamily: 'Manrope, sans-serif', marginBottom: 8 }}>Message sent</h3>
                <p style={{ fontSize: 15, color: 'var(--land-muted)', marginBottom: 28 }}>We'll get back to you within 24 hours.</p>
                <button className="lp-btn-ghost" onClick={() => setSent(false)}>Send another message</button>
              </div>
            ) : (
              <>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--land-text)', fontFamily: 'Manrope, sans-serif', marginBottom: 28 }}>
                  Send a message
                </h2>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    {[
                      { name: 'name', label: 'Full name', placeholder: 'Kwame Mensah', required: true },
                      { name: 'email', label: 'Email', placeholder: 'kwame@school.edu', required: true, type: 'email' },
                    ].map(f => (
                      <div key={f.name}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--land-text)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{f.label}</label>
                        <input
                          type={f.type || 'text'}
                          name={f.name}
                          value={formData[f.name]}
                          onChange={handleChange}
                          required={f.required}
                          placeholder={f.placeholder}
                          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--land-border)', fontSize: 14, color: 'var(--land-text)', background: 'var(--land-off)', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                          onFocus={e => e.target.style.borderColor = 'var(--land-moss)'}
                          onBlur={e => e.target.style.borderColor = 'var(--land-border)'}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    {[
                      { name: 'phone', label: 'Phone', placeholder: '+233 53 448 4781' },
                      { name: 'school', label: 'School name', placeholder: 'Your school' },
                    ].map(f => (
                      <div key={f.name}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--land-text)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{f.label}</label>
                        <input
                          type="text"
                          name={f.name}
                          value={formData[f.name]}
                          onChange={handleChange}
                          placeholder={f.placeholder}
                          style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--land-border)', fontSize: 14, color: 'var(--land-text)', background: 'var(--land-off)', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                          onFocus={e => e.target.style.borderColor = 'var(--land-moss)'}
                          onBlur={e => e.target.style.borderColor = 'var(--land-border)'}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--land-text)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell us how we can help..."
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--land-border)', fontSize: 14, color: 'var(--land-text)', background: 'var(--land-off)', outline: 'none', resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
                      onFocus={e => e.target.style.borderColor = 'var(--land-moss)'}
                      onBlur={e => e.target.style.borderColor = 'var(--land-border)'}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="lp-btn-primary-lg"
                    style={{ justifyContent: 'center', opacity: loading ? 0.6 : 1 }}
                  >
                    {loading ? 'Sending…' : 'Send Message'}
                    {!loading && <ArrowRight size={17} weight="bold" />}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--land-border)', padding: '24px 32px', textAlign: 'center', background: 'var(--land-off)' }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--land-muted)' }}>
          &larr; Back to Campusio
        </button>
      </div>
    </div>
  );
}
